/**
 * BatchCreationHelper.js
 * 
 * Helper functions for creating and managing batches for the virtual deck system.
 * Used by the game creation wizard and other components.
 */

import { generateSecureSeed, generateSecureSalt } from './VirtualDeckGenerator';
import { createSegmentedBatch, generateBatchId, getOrCreateBatch } from './SegmentManager';

/**
 * Creates a new batch for a game with the specified configuration
 * @param {Object} gameConfig - The game configuration
 * @returns {Object} The created batch
 */
export const createInitialBatch = async (gameConfig) => {
  try {
    // Generate secure random seed and salt
    const seed = await generateSecureSeed();
    const salt = await generateSecureSalt();
    
    // Create a batch ID based on the game ID
    const batchId = `${gameConfig.id}_batch_${Date.now()}`;
    
    // Create the prize distribution based on the game's prize tiers
    const prizeDistribution = [];
    
    if (gameConfig.prizeTiers && gameConfig.prizeTiers.length > 0) {
      gameConfig.prizeTiers.forEach(tier => {
        for (let i = 0; i < tier.count; i++) {
          prizeDistribution.push({
            tier: tier.id,
            amount: tier.amount,
            name: tier.name || `${tier.amount} ${gameConfig.priceCurrency || 'SOL'}`
          });
        }
      });
    }
    
    // Fill the rest with non-winning cards
    const nonWinningCount = gameConfig.batchSize - prizeDistribution.length;
    for (let i = 0; i < nonWinningCount; i++) {
      prizeDistribution.push({
        tier: 'non_winning',
        amount: 0,
        name: 'Non-winning'
      });
    }
    
    // Create the batch
    const batch = await createSegmentedBatch({
      id: batchId,
      gameId: gameConfig.id,
      size: gameConfig.batchSize,
      segmentSize: gameConfig.segmentSize,
      seed,
      salt,
      prizeDistribution,
      metadata: {
        gameName: gameConfig.title,
        gameType: gameConfig.baseConfig?.gameType || 'custom',
        createdAt: new Date().toISOString(),
        verificationRequired: gameConfig.verificationRequired,
        verificationDelay: gameConfig.verificationDelay,
        useCommitReveal: gameConfig.useCommitReveal
      }
    });
    
    // Segments are automatically created by createSegmentedBatch
    
    return batch;
  } catch (error) {
    console.error('Error creating initial batch:', error);
    throw error;
  }
};

/**
 * Gets the active batch for a game
 * @param {string} gameId - The game ID
 * @returns {Object|null} The active batch or null if none exists
 */
export const getActiveBatchForGame = (gameId) => {
  try {
    // Get all batches from localStorage
    const batchesJson = localStorage.getItem('batches');
    if (!batchesJson) return null;
    
    const batches = JSON.parse(batchesJson);
    
    // Find the most recent batch for this game
    const gameBatches = batches.filter(batch => batch.gameId === gameId);
    if (gameBatches.length === 0) return null;
    
    // Sort by creation date (newest first)
    gameBatches.sort((a, b) => {
      const dateA = new Date(a.metadata.createdAt);
      const dateB = new Date(b.metadata.createdAt);
      return dateB - dateA;
    });
    
    return gameBatches[0];
  } catch (error) {
    console.error('Error getting active batch for game:', error);
    return null;
  }
};
